import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  SELECTION_CHANGE_COMMAND,
  FORMAT_TEXT_COMMAND,
  $getSelection,
  $isRangeSelection,
  $isNodeSelection,
  FORMAT_ELEMENT_COMMAND,
  ElementFormatType,
  $isElementNode
} from "lexical";
import { $isLinkNode, TOGGLE_LINK_COMMAND } from "@lexical/link";

import { $getNearestNodeOfType, mergeRegister } from "@lexical/utils";
import {
  INSERT_CHECK_LIST_COMMAND,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
  $isListNode,
  ListNode
} from "@lexical/list";
import { $isHeadingNode } from "@lexical/rich-text";
import Icon from "@/components/Icon";
import { createPortal } from "react-dom";
import { getSelectedNode } from "../utils";
import FloatingLinkEditor from "./FloatingLinkEditor";
import { InsertImageDialog } from "@/lib/lexical/plugins/ImagesPlugin";

const LowPriority = 1;

interface ToolbarPluginProps {
  enableHtmlFeatures: boolean;
  enableImageUpload: boolean;
}

export default function ToolbarPlugin({ enableHtmlFeatures = false, enableImageUpload = false }: ToolbarPluginProps) {
  const [editor] = useLexicalComposerContext();
  const toolbarRef = useRef(null);
  const [blockType, setBlockType] = useState("paragraph");
  const [elementFormat, setElementFormat] = useState<ElementFormatType>("");
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isLink, setIsLink] = useState(false);
  const [showImageDialog, setShowImageDialog] = useState(false);

  const updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if (selection !== null && $isRangeSelection(selection)) {
      const anchorNode = selection.anchor.getNode();
      const element = anchorNode.getKey() === "root" ? anchorNode : anchorNode.getTopLevelElementOrThrow();
      const elementKey = element.getKey();
      const elementDOM = editor.getElementByKey(elementKey);

      if ($isElementNode(element)) {
        setElementFormat(element.getFormatType());
      } else {
        setElementFormat("");
      }

      if (elementDOM !== null) {
        if ($isListNode(element)) {
          const parentList = $getNearestNodeOfType(anchorNode, ListNode);
          const type = parentList ? parentList.getListType() : element.getListType();
          setBlockType(type);
        } else {
          const type = $isHeadingNode(element) ? element.getTag() : element.getType();
          setBlockType(type);
        }
      }
      const updateFormats = () => {
        setIsBold(selection.hasFormat("bold"));
        setIsItalic(selection.hasFormat("italic"));
        setIsUnderline(selection.hasFormat("underline"));
      };

      const updateLinks = () => {
        const node = getSelectedNode(selection);
        const parent = node.getParent();
        if ($isLinkNode(parent) || $isLinkNode(node)) {
          setIsLink(true);
        } else {
          setIsLink(false);
        }
      };

      updateFormats();
      updateLinks();
    }
  }, [editor]);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          updateToolbar();
        });
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        (_payload) => {
          updateToolbar();
          return false;
        },
        LowPriority
      )
    );
  }, [editor, updateToolbar]);

  const formatBulletList = () => {
    if (blockType !== "bullet") {
      editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
    } else {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
    }
  };

  const formatNumberedList = () => {
    if (blockType !== "number") {
      editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
    } else {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
    }
  };

  const formatCheckList = () => {
    if (blockType !== "check") {
      editor.dispatchCommand(INSERT_CHECK_LIST_COMMAND, undefined);
    } else {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
    }
  };

  const insertLink = useCallback(() => {
    if (!isLink) {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, "https://");
    } else {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
    }
  }, [editor, isLink]);

  const insertImage = useCallback(() => {
    setShowImageDialog(true);
  }, []);

  const applyAlignment = useCallback(
    (format: ElementFormatType) => {
      editor.update(() => {
        const selection = $getSelection();
        if (selection !== null && $isNodeSelection(selection)) {
          // For node selections (e.g., an image), apply alignment to the parent element (usually a paragraph)
          const node = selection.getNodes()[0];
          const parent = node.getParent();
          if ($isElementNode(parent)) {
            parent.setFormat(format);
          }
        } else {
          // Range selection – use the built-in command
          editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, format);
        }
      });
    },
    [editor]
  );

  return (
    <div
      className="flex flex-row gap-1 text-sys-brand-on-surface px-3 py-2 border-b border-sys-brand-outline"
      ref={toolbarRef}
    >
      <button
        onClick={() => {
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, "bold");
        }}
        className={"h-6 w-6 rounded-md " + (isBold ? "bg-sys-brand-secondary-container" : "")}
        aria-label="Format Bold"
        data-testid="bold-button"
      >
        <Icon name="format_bold" />
      </button>
      <button
        onClick={() => {
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, "italic");
        }}
        className={"h-6 w-6 rounded-md " + (isItalic ? "bg-sys-brand-secondary-container" : "")}
        aria-label="Format Italics"
      >
        <Icon name="format_italic" />
      </button>
      <button
        onClick={() => {
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, "underline");
        }}
        className={"h-6 w-6 rounded-md " + (isUnderline ? "bg-sys-brand-secondary-container" : "")}
        aria-label="Format Underline"
      >
        <Icon name="format_underlined" />
      </button>
      <button
        className={"h-6 w-6 rounded-md " + (isLink ? "bg-sys-brand-secondary-container" : "")}
        onClick={insertLink}
        aria-label="Insert Link"
      >
        <Icon name="link" />
      </button>
      {isLink && createPortal(<FloatingLinkEditor editor={editor} />, document.body)}
      <button
        className={"ml-6 h-6 w-6 rounded-md " + (blockType === "bullet" ? "bg-sys-brand-secondary-container" : "")}
        onClick={formatBulletList}
        aria-label="Format Bullet List"
        data-testid="bullet-list-button"
      >
        <Icon name="format_list_bulleted" />
      </button>
      <button
        className={"h-6 w-6 rounded-md " + (blockType === "number" ? "bg-sys-brand-secondary-container" : "")}
        onClick={formatNumberedList}
        aria-label="Format Numbered List"
        data-testid="numbered-list-button"
      >
        <Icon name="format_list_numbered" />
      </button>
      <button
        className={"h-6 w-6 rounded-md " + (blockType === "check" ? "bg-sys-brand-secondary-container" : "")}
        onClick={formatCheckList}
        aria-label="Format Checklist"
        data-testid="checklist-button"
      >
        <Icon name="checklist" />
      </button>
      {enableHtmlFeatures && (
        <>
          <button
            onClick={() => applyAlignment("left")}
            className={"h-6 w-6 rounded-md " + (elementFormat === "left" ? "bg-sys-brand-secondary-container" : "")}
            aria-label="Left Align"
          >
            <Icon name="format_align_left" />
          </button>
          <button
            onClick={() => applyAlignment("center")}
            className={"h-6 w-6 rounded-md " + (elementFormat === "center" ? "bg-sys-brand-secondary-container" : "")}
            aria-label="Center Align"
          >
            <Icon name="format_align_center" />
          </button>
          <button
            onClick={() => applyAlignment("right")}
            className={"h-6 w-6 rounded-md " + (elementFormat === "right" ? "bg-sys-brand-secondary-container" : "")}
            aria-label="Right Align"
          >
            <Icon name="format_align_right" />
          </button>
          <button
            onClick={() => applyAlignment("justify")}
            className={"h-6 w-6 rounded-md " + (elementFormat === "justify" ? "bg-sys-brand-secondary-container" : "")}
            aria-label="Justify Align"
          >
            <Icon name="format_align_justify" />
          </button>
          {enableImageUpload && (
            <>
              <div className="h-6 border-l-2 border-sys-brand-secondary mx-2"></div>
              <button className="h-6 w-6 rounded-md" onClick={insertImage}>
                <Icon name="image" />
              </button>
              {showImageDialog &&
                createPortal(
                  <InsertImageDialog activeEditor={editor} onClose={() => setShowImageDialog(false)} />,
                  document.body
                )}
            </>
          )}
        </>
      )}
    </div>
  );
}
